import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  ThemeProvider,
  createTheme,
  Box,
  Tabs,
  Tab,
  Button,
  Alert,
  Snackbar,
} from '@mui/material';
import ErrorBoundary from './components/common/ErrorBoundary';
import AccountMenu from './components/common/AccountMenu';
import WelcomeDialog from './components/auth/WelcomeDialog';
import ConversionsTab from './components/tabs/ConversionsTab';
import RecommendationTab from './components/tabs/RecommendationTab';
import LandingTab from './components/tabs/LandingTab';
import ConceptsTab from './components/tabs/ConceptsTab';
import CorePrinciplesTab from './components/tabs/CorePrinciplesTab';
import AboutTab from './components/tabs/AboutTab';
// import RatingsTab from './components/tabs/RatingsTab';
import { useAuth, useUserData, useInputData, useConversions } from './components/hooks/useAppData';
import apiService from './services/apiService';
import './App.css';

const theme = createTheme();

const TAB_NAMES = ['home', 'conversions', 'recommendation', 'concepts', 'principles', 'about']; // 'ratings' commented out. 2026-05-06: swapped conversions/recommendation positions; URL hash 'recommendation' still maps to Conversion Detail

const getTabFromHash = () => {
  const hash = window.location.hash.replace('#', '');
  const [tabName, param] = hash.split('/');
  const index = TAB_NAMES.indexOf(tabName);
  return { tabIndex: index >= 0 ? index : null, groupNum: param ? parseInt(param) : null };
};

function App() {
  // Custom hooks for data management
  const { userId, isLoggedIn, login, logout: authLogout, createUser, loginWithWorkOS } = useAuth();
  const { userInfo, loading: userLoading, error: userError, refetch: refetchUser } = useUserData(userId);
  const { inputInfo, runId, loading: inputLoading, error: inputError, updateInputs, toggleTaxPaymentSource, toggleUseIRSBrackets, refetch: refetchInputs } = useInputData(userId);
  const { conversions, parts, retireYearData, distributionSchedule, loading: conversionsLoading, error: conversionsError, runCalculations } = useConversions(runId);

  // Tab state - read from hash first, fall back to localStorage
  const [activeTab, setActiveTab] = useState(() => {
    const { tabIndex } = getTabFromHash();
    if (tabIndex !== null) return tabIndex;
    const saved = localStorage.getItem('activeTab');
    return saved ? parseInt(saved) : 2;  // 2026-05-06: Default to Conversion Detail tab (index 2 after swap)
  });
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  // State for selected conversion (for Recommendation tab)
  const [selectedConversion, setSelectedConversion] = useState(null);
  const [isRecommendedConversion, setIsRecommendedConversion] = useState(true);
  // Refresh selectedConversion with new data when conversions update (e.g., after tax source toggle)
  useEffect(() => {
    if (selectedConversion && conversions?.length > 0) {
      const updated = conversions.find(c => c.conv_group_num === selectedConversion.conv_group_num);
      if (updated && updated !== selectedConversion) {
        setSelectedConversion(updated);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversions]);

  // State to trigger opening the assumptions dialog on Conversions tab
  const [openAssumptionsDialog, setOpenAssumptionsDialog] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeSubmitting, setWelcomeSubmitting] = useState(false);
  // State to trigger auto-emailing report for new users
  const [autoEmailReport, setAutoEmailReport] = useState(false);
  // Auto-email the welcome report once a new user's first run produces a runId, regardless of
  // which tab they're on (previously only fired when the Recommendation tab mounted).
  useEffect(() => {
    if (autoEmailReport && runId) {
      apiService.emailReport(runId)
        .then(() => console.log('Welcome report emailed'))
        .catch((err) => console.error('Failed to auto-email report:', err));
      setAutoEmailReport(false);
    }
  }, [autoEmailReport, runId]);
  // 2026-04-02: Session expired message from single-session enforcement
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState(null);
  // State for scrolling to a specific chart on Core Principles tab
  const [scrollToChart, setScrollToChart] = useState(null);
  // 2026-05-08: Lifted from RecommendationTab so dropdown view survives tab switches (browser back returns to same view)
  const [recYearlyView, setRecYearlyView] = useState('income_tax');
  const [recShowComparison, setRecShowComparison] = useState(false);

  // Save tab to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab.toString());
  }, [activeTab]);

  // Browser back/forward support
  useEffect(() => {
    const handlePopState = () => {
      const { tabIndex, groupNum } = getTabFromHash();
      setActiveTab(tabIndex !== null ? tabIndex : 0);
      // Restore conversion selection when navigating back/forward on Conversion Detail tab (index 2 after 2026-05-06 swap)
      if (tabIndex === 2 && groupNum !== null && conversions) {
        const conv = conversions.find(c => c.conv_group_num === groupNum);
        if (conv) {
          setSelectedConversion(conv);
          setIsRecommendedConversion(conv.conv_group_num === getRecommendedConversion()?.conv_group_num);
        }
      } else if (tabIndex === 2) {
        // No group in URL — show default recommended
        setSelectedConversion(null);
        setIsRecommendedConversion(true);
      }
      window.scrollTo(0, 0);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversions]);

  // Push tab to browser history (for user-initiated navigation)
  const navigateToTab = (tabIndex) => {
    setActiveTab(tabIndex);
    window.history.pushState({ tab: tabIndex }, '', `#${TAB_NAMES[tabIndex]}`);
    window.scrollTo(0, 0);
  };

  // Check for payment success on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setPaymentSuccess(true);
      setActiveTab(1); // Switch to Conversions tab (index 1 after 2026-05-06 swap)
      // Refetch user data to get updated subscription status
      refetchUser();
      // Clear the query parameter from URL, set hash to conversions
      window.history.replaceState({ tab: 2 }, document.title, window.location.pathname + '#conversions');
      // Clear success message after 5 seconds
      setTimeout(() => setPaymentSuccess(false), 5000);
    }
  }, [refetchUser]);

  // 2026-04-02: Listen for session-expired event from axios interceptor (single-session enforcement)
  useEffect(() => {
    const handler = () => {
      authLogout();
      localStorage.removeItem('activeTab');
      setActiveTab(0);
      window.history.replaceState({ tab: 0 }, '', '#home');
      // Brief delay so state clears before showing message (dialog unmounts first)
      setTimeout(() => {
        setSessionExpiredMessage('You have been logged out — your account was accessed from another device.');
      }, 100);
    };
    window.addEventListener('session-expired', handler);
    return () => window.removeEventListener('session-expired', handler);
  }, [authLogout]);

  // 2026-01-03: Redirect logged-out users from login-required tabs (Recommendation, Conversions)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isPaymentReturn = params.get('payment') === 'success'; // 2026-01-03
    if (!isLoggedIn && !isPaymentReturn && (activeTab === 1 || activeTab === 2)) {
      setActiveTab(0);
      window.history.replaceState({ tab: 0 }, '', '#home');
    }
  }, [isLoggedIn, activeTab]);

  // 2026-03-29: On initial load only, redirect logged-out users from stale localStorage tabs 3-4 to HOME
  useEffect(() => {
    if (!isLoggedIn && (activeTab === 3 || activeTab === 4)) {
      setActiveTab(0);
      window.history.replaceState({ tab: 0 }, '', '#home');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Logout handler that also switches to Landing tab
  // 2026-01-03: Modified to clear saved tab from localStorage on logout
  const handleLogout = () => {
    authLogout();
    localStorage.removeItem('activeTab'); // 2026-01-03: Clear saved tab on logout
    setActiveTab(0);
    window.history.replaceState({ tab: 0 }, '', '#home');
    // 2026-06-17: mark an explicit logout so the WorkOS bridge doesn't immediately re-log the user
    // back in. The WorkOS session itself persists (re-login is one click). A full WorkOS sign-out
    // redirect needs a logout-redirect URL configured in the dashboard — deferred. (Phase 3)
    sessionStorage.setItem('workosLoggedOut', '1');
  };
  // Original code before 2026-01-03:
  // const handleLogout = () => {
  //   authLogout();
  //   setActiveTab(0);
  // };

  // Determine if loading
  const isLoading = userLoading || inputLoading || conversionsLoading;
  const hasError = userError || inputError || conversionsError;

  // Handle login (passed to LoginForm)
  const handleLogin = async (credentials) => {
    await login(credentials);
    setActiveTab(2);  // 2026-05-06: Land on Conversion Detail tab (index 2 after swap) after login
  };

  // 2026-06-17: WorkOS web login → exchange token for an app session, then land in the app (Phase 3)
  const handleWorkOSLogin = async (accessToken) => {
    const result = await loginWithWorkOS(accessToken);
    if (result?.created) {
      setShowWelcome(true);  // brand-new account → guided first-login onboarding
    } else {
      setActiveTab(2);  // returning user → straight into the app
    }
  };

  // 2026-06-17: new-user onboarding — save birth date + filing status (+ spouse birth date), then
  // open the inputs screen for the financial numbers (Phase 3). marital_status mirrors the chosen
  // filing status, which is how the inputs screen reads it.
  const handleWelcomeComplete = async ({ birth_date, distribution_status, birth_date_spouse }) => {
    setWelcomeSubmitting(true);
    try {
      await apiService.updateUserProfile(userId, {
        email: userInfo?.email,
        birth_date,
        marital_status: distribution_status,
        birth_date_spouse,
      });
      await refetchUser();
      setShowWelcome(false);
      setOpenAssumptionsDialog(true);
      setAutoEmailReport(true);
      navigateToTab(1);
    } catch (e) {
      console.error('Onboarding save failed', e);
    } finally {
      setWelcomeSubmitting(false);
    }
  };

  // Handle user creation (passed to LoginForm)
  const handleCreateUser = async (userData) => {
    await createUser(userData);
    // Navigate new users to CONVERSIONS tab (index 1) with assumptions dialog open.
    // 2026-06-11: was navigateToTab(2) — stale after 2026-05-06 swap made index 2
    // "Conversion Detail"; the assumptions dialog only renders on Conversions (index 1).
    setOpenAssumptionsDialog(true);
    setAutoEmailReport(true);
    navigateToTab(1);
  };

  // Handle tab change (user clicking tabs)
  const handleTabChange = (event, newValue) => {
    navigateToTab(newValue);
  };

  // Wrapper for runCalculations that refreshes inputs afterwards
  const handleRunCalculations = async (userId) => {
    const result = await runCalculations(userId);
    await refetchInputs(); // Refresh inputs to get updated run_id
    return result;
  };

  // Get the recommended conversion (last group before first red row in parts table)
  // The level with the highest cumulative alpha, or null when none is positive. A maximum rather than a scan
  // for the first negative slice: alpha is not monotonic across brackets, since an IRMAA tier, the SS torpedo
  // zone, or the OBBBA senior-bonus phaseout can dip it mid-ladder and recover. Must stay in step with
  // generate_report._best_by_alpha.
  const getRecommendedConversion = () => {
    const eligible = (conversions || []).filter(c => c.conv_amt > 0);
    if (eligible.length === 0) return null;
    const best = eligible.reduce((a, b) => ((b.conversion_alpha_pv || 0) > (a.conversion_alpha_pv || 0) ? b : a));
    return (best.conversion_alpha_pv || 0) > 0 ? best : null;
  };

  // What CONVERSION DETAIL shows when there is no recommendation: the smallest conversion available, so the
  // tab still renders. Separate from the recommendation itself, which stays null and is stated as such.
  const getDisplayConversion = () =>
    getRecommendedConversion() || (conversions || []).find(c => c.conv_amt > 0) || null;

  // Handle navigation to Recommendation tab with a specific conversion
  const handleViewConversion = (conversion, isRecommended = false) => {
    setSelectedConversion(conversion);
    setIsRecommendedConversion(isRecommended);
    setActiveTab(2);
    window.history.pushState({ tab: 2, group: conversion.conv_group_num }, '', `#recommendation/${conversion.conv_group_num}`);
    window.scrollTo(0, 0);
  };

  // Handle viewing recommended conversion
  const handleViewRecommended = () => {
    const recommended = getRecommendedConversion();
    if (recommended) handleViewConversion(recommended, true);
  };

  return (
    <ThemeProvider theme={theme}>
      <ErrorBoundary>
        {/* Header with Title and Account Icon - full width blue */}
        <Box style={{ backgroundColor: '#e3f2fd' }}>
          <Container maxWidth="lg" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 24px 0px 24px' }}>
            <Typography variant="h5">
              Roth <strong>G</strong>auged <strong>P</strong>rofit <strong>T</strong>iming - RothGPT
            </Typography>
            {/* Account Menu - top right */}
            {/* 2026-09-13: onProfileUpdate refreshes inputs too — a profile marital-status
                change now also writes inputs.distribution_status, so reading only the user
                left the input screen showing the old filing status until a page reload. */}
            {isLoggedIn && (
              <AccountMenu
                userId={userId}
                userInfo={userInfo}
                onProfileUpdate={async () => { await refetchUser(); await refetchInputs(); }}
                onLogout={handleLogout}
              />
            )}
          </Container>
        </Box>

        {/* Tab Navigation - full width blue, sticky */}
        <Box style={{ borderBottom: '1px solid #ddd', position: 'sticky', top: 0, zIndex: 100, backgroundColor: '#e3f2fd' }}>
          <Container maxWidth="lg" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: '24px', paddingRight: '24px' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="standard"
              style={{ backgroundColor: 'transparent' }}
              sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
            >
              <Tab label="Home |" sx={{ fontWeight: 'bold' }} />
              <Tab label="Conversions |" disabled={!isLoggedIn} sx={{ fontWeight: isLoggedIn ? 'bold' : 'normal' }} />
              <Tab label="Conversion Detail |" disabled={!isLoggedIn} sx={{ fontWeight: isLoggedIn ? 'bold' : 'normal' }} />
              <Tab label="Concepts |" sx={{ fontWeight: 'bold' }} />
              <Tab label="Principles |" sx={{ fontWeight: 'bold' }} />
              <Tab label="About" sx={{ fontWeight: 'bold' }} />
              {/* <Tab label="Ratings" disabled={!isLoggedIn} sx={{ fontWeight: isLoggedIn ? 'bold' : 'normal' }} /> */}
            </Tabs>

            {/* Logout Button */}
            {isLoggedIn && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleLogout}
                size="small"
                style={{ marginRight: '10px' }}
              >
                Logout
              </Button>
            )}
          </Container>
        </Box>

        <Container maxWidth="lg">
          {/* Payment Success Message */}
          {paymentSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Payment successful! Thank you for your support. You can now run unlimited calculations.
            </Alert>
          )}

          {/* 2026-06-17: first-login onboarding for brand-new WorkOS accounts */}
          <WelcomeDialog
            open={showWelcome}
            onComplete={handleWelcomeComplete}
            submitting={welcomeSubmitting}
          />

          {/* Tab Panels */}
          {activeTab === 0 && (
            <Box className="tab-panel">
              <LandingTab
                isLoggedIn={isLoggedIn}
                loading={isLoggedIn && isLoading}
                onLogin={handleLogin}
                onCreateUser={handleCreateUser}
                onWorkOSLogin={handleWorkOSLogin}
              />
            </Box>
          )}

          {activeTab === 2 && (
            <Box className="tab-panel">
              <RecommendationTab
                conversion={(selectedConversion?.conv_amt > 0 ? selectedConversion : null) || getDisplayConversion()}
                recommendedConversion={getRecommendedConversion()}
                distributionSchedule={distributionSchedule}
                inputInfo={inputInfo}
                userInfo={userInfo}
                runId={runId}
                parts={parts}
                conversions={conversions}
                isRecommended={isRecommendedConversion || !selectedConversion}
                hasData={conversions && conversions.length > 0}
                onViewAllConversions={() => navigateToTab(1)}
                onAddAssumptions={() => {
                  setOpenAssumptionsDialog(true);
                  navigateToTab(1);
                }}
                onViewRecommended={handleViewRecommended}
                autoEmailReport={autoEmailReport}
                setAutoEmailReport={setAutoEmailReport}
                onNavigateToChart={(chartId) => {
                  setScrollToChart(chartId);
                  navigateToTab(4);
                }}
                onToggleTaxSource={async (newValue) => {
                  await toggleTaxPaymentSource(newValue, userInfo);
                  await handleRunCalculations(userId);
                  // Don't clear selectedConversion — useEffect on conversions
                  // will update it with new data for the same group_num
                }}
                onToggleUseIRSBrackets={async (newValue) => {
                  await toggleUseIRSBrackets(newValue, userInfo);
                  await handleRunCalculations(userId);
                }}
                selectedYearlyView={recYearlyView}
                setSelectedYearlyView={setRecYearlyView}
                showComparison={recShowComparison}
                setShowComparison={setRecShowComparison}
              />
            </Box>
          )}

          {activeTab === 1 && (
            <Box className="tab-panel">
              <ConversionsTab
                userId={userId}
                userInfo={userInfo}
                inputInfo={inputInfo}
                conversions={conversions}
                parts={parts}
                retireYearData={retireYearData}
                distributionSchedule={distributionSchedule}
                loading={isLoading}
                error={hasError}
                onUpdateInputs={updateInputs}
                onRunCalculations={handleRunCalculations}
                onRefetchUser={refetchUser}
                onViewConversion={handleViewConversion}
                onViewRecommended={handleViewRecommended}
                recommendedConversion={getRecommendedConversion()}
                openAssumptionsDialog={openAssumptionsDialog}
                setOpenAssumptionsDialog={setOpenAssumptionsDialog}
                onNavigateToTab={navigateToTab}
                onToggleTaxSource={async (newValue) => {
                  await toggleTaxPaymentSource(newValue, userInfo);
                  await handleRunCalculations(userId);
                }}
                onToggleUseIRSBrackets={async (newValue) => {
                  await toggleUseIRSBrackets(newValue, userInfo);
                  await handleRunCalculations(userId);
                }}
              />
            </Box>
          )}

          {activeTab === 3 && (
            <Box className="tab-panel">
              <ConceptsTab inputInfo={inputInfo} userInfo={userInfo} retireYearData={retireYearData} recommendedConversion={selectedConversion || getDisplayConversion()} />
            </Box>
          )}

          {activeTab === 4 && (
            <Box className="tab-panel">
              <CorePrinciplesTab inputInfo={inputInfo} userInfo={userInfo} retireYearData={retireYearData} recommendedConversion={selectedConversion || getDisplayConversion()} scrollToChart={scrollToChart} clearScrollToChart={() => setScrollToChart(null)} />
            </Box>
          )}

          {activeTab === 5 && (
            <Box className="tab-panel">
              <AboutTab />
            </Box>
          )}

          {/* {activeTab === 5 && (
            <Box className="tab-panel">
              <RatingsTab userId={userId} />
            </Box>
          )} */}
        </Container>
      </ErrorBoundary>
      {/* 2026-04-02: Session expired notification */}
      <Snackbar
        open={!!sessionExpiredMessage}
        autoHideDuration={10000}
        onClose={() => setSessionExpiredMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setSessionExpiredMessage(null)} sx={{ width: '100%' }}>
          {sessionExpiredMessage}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default App;